import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Breadcrumbs,
  Link,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  Home as HomeIcon,
  Videocam as VideocamIcon,
  LocalParking as LocalParkingIcon,
  Lightbulb as LightbulbIcon,
  NotificationsActive as NotificationsActiveIcon,
  Checklist as ChecklistIcon,
  PlaylistPlay as AutoRunIcon,
  Stop as StopIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { SimulatorService } from '../../services/SimulatorService';
import FullVacancyCheckSimulator from './FullVacancyCheckSimulator';
import type { CctvParkingLink, CctvLightLink, MapCctv, MapBell } from './mapAssetFix';
import LightsSimulator from './LightsSimulator';
import EmergencyBellSimulator from './EmergencyBellSimulator';
import MapCanvasPanel from './MapCanvasPanel';
import AutoTestReportDialog from './AutoTestReportDialog';
import type { AutoCheckResult, SimAutoRunHandle } from './autoTestUtils';
import {
  SimulatorCctv,
  SimulatorEmergencyBell,
  SimulatorMap,
  SimulatorSession,
  SimulatorStatus,
} from '../../models/Simulator';

const POLL_INTERVAL_MS = 5000;

type ChipColor = 'success' | 'warning' | 'error' | 'default';

const statusColor = (status: SimulatorStatus | undefined): ChipColor => {
  if (status === 'running') return 'success';
  if (status === 'starting') return 'warning';
  if (status === 'error') return 'error';
  if (status === 'stopped') return 'default';
  return 'default';
};

const statusLabel = (status: SimulatorStatus | undefined): string => {
  if (status === 'starting') return '시작 중';
  if (status === 'running') return '실행 중';
  if (status === 'stopped') return '중지됨';
  if (status === 'error') return '오류';
  return '-';
};

const isPolling = (status: SimulatorStatus | undefined): boolean =>
  status === 'starting' || status === 'running';

/**
 * Simulator page.
 *
 * 흐름:
 * - 마운트 시 사이트의 맵 목록(`/v0.1/map-editor/projects?site_project_id=`) 을 로드해 드롭다운 채움.
 * - 첫 항목 자동 선택 → 그 맵의 CCTV(`/v0.1/simulator/maps/:mapId/cctvs`) 표시.
 * - "시작" → backend 가 선택한 맵의 lightCctvs 를 다시 조회해 mediamtx path 를 구성.
 * - status 폴링은 starting/running 동안만, stopped/error 또는 unmount 시 정리.
 */
const SimulatorPage: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams<{ projectId: string }>();
  const projectId = Number(params.projectId);

  // 헤더 브레드크럼에 표시할 현장 이름 — 다른 페이지(SitePhotosPage)와 동일하게
  // 현장 선택 시 저장된 selectedProject 에서 읽고, 없으면 id 로 폴백.
  const storedProject = localStorage.getItem('selectedProject');
  const projectName = storedProject
    ? (JSON.parse(storedProject).name as string | undefined)
    : undefined;

  const [tab, setTab] = useState(0);

  // ── 통합 자동 테스트(탭 4) — 조명/만공차등/비상벨을 순서대로 하나씩 실행하고
  // 결과를 합쳐서 리포트로 보여준다. useLightsCaptureSession 이 "동시에 2개
  // 마운트되지 않는다"를 전제하므로, 항상 한 번에 하나만 마운트한다(아래
  // combinedMounted). 해당 탭이 이미 열려 있으면(alreadyMounted) 그 인스턴스의
  // ref 를 그대로 재사용하고 별도로 마운트하지 않는다.
  type CombinedKey = 'lights' | 'parking' | 'bell';
  const COMBINED_ORDER: CombinedKey[] = ['lights', 'parking', 'bell'];
  const COMBINED_LABEL: Record<CombinedKey, string> = { lights: '조명', parking: '만공차등', bell: '비상벨' };
  const COMBINED_TAB: Record<CombinedKey, number> = { lights: 2, parking: 1, bell: 3 };
  const [combinedTargets, setCombinedTargets] = useState<Record<CombinedKey, boolean>>({
    lights: true,
    parking: true,
    bell: true,
  });
  const [combinedRunning, setCombinedRunning] = useState(false);
  const [combinedProgress, setCombinedProgress] = useState('');
  const [combinedReport, setCombinedReport] = useState<AutoCheckResult[] | null>(null);
  const [combinedShowReport, setCombinedShowReport] = useState(false);
  // 오케스트레이터가 현재 화면 밖에 임시로 마운트해둔 시뮬레이터(그 탭이 열려있지 않을 때만).
  const [combinedMounted, setCombinedMounted] = useState<CombinedKey | null>(null);
  const combinedStopRef = useRef(false);
  const lightsAutoRef = useRef<SimAutoRunHandle>(null);
  const parkingAutoRef = useRef<SimAutoRunHandle>(null);
  const bellAutoRef = useRef<SimAutoRunHandle>(null);
  const combinedRefs: Record<CombinedKey, React.RefObject<SimAutoRunHandle>> = {
    lights: lightsAutoRef,
    parking: parkingAutoRef,
    bell: bellAutoRef,
  };
  const [maps, setMaps] = useState<SimulatorMap[]>([]);
  const [selectedMapId, setSelectedMapId] = useState<number | ''>('');
  const [loadingMaps, setLoadingMaps] = useState(false);

  const [session, setSession] = useState<SimulatorSession | null>(null);
  const [cctvs, setCctvs] = useState<SimulatorCctv[]>([]);
  const [bells, setBells] = useState<SimulatorEmergencyBell[]>([]);
  // 맵에 실제 배치된 CCTV 이름(MapCanvasPanel 이 graphJson 에서 추출해 보고).
  // 시뮬레이터 CCTV 목록 API 가 비어 있어도 맵 CCTV 로 선택/입력하게 한다.
  const [mapCctvNames, setMapCctvNames] = useState<string[]>([]);
  // 맵 CCTV 이름+IP — 비상벨 탭 "이름 (IP)" 다중 선택지.
  const [mapCctvList, setMapCctvList] = useState<MapCctv[]>([]);
  // CCTV↔주차면 1:n 관계(만공차 체크 탭에서 사용).
  const [cctvParkingLinks, setCctvParkingLinks] = useState<CctvParkingLink[]>([]);
  // CCTV↔조명 관계(비상벨 탭 — 호출 시 연결 조명 점멸).
  const [cctvLightLinks, setCctvLightLinks] = useState<CctvLightLink[]>([]);
  // 맵 graphJson 에서 추출한 비상벨 — 비상벨 API 가 빈 목록일 때 폴백.
  const [mapBells, setMapBells] = useState<MapBell[]>([]);
  // 비상벨 목록: API(export 형식 map_data) 우선, 비어 있으면 맵 추출본 사용.
  // 현장 저장본은 raw graphJson 이라 API 가 빈 목록을 주는 경우가 대부분이다.
  const effectiveBells = useMemo<SimulatorEmergencyBell[]>(
    () => (bells.length > 0 ? bells : mapBells),
    [bells, mapBells],
  );
  const [loadingStart, setLoadingStart] = useState(false);
  const [loadingStop, setLoadingStop] = useState(false);
  const [loadingCctvs, setLoadingCctvs] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // 폴링 타이머 — 단일 setInterval, unmount 또는 종료 상태 도달 시 정리.
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // status fetch 가 set 호출 전에 stale closure 를 잡지 않도록 최신 session_id 참조.
  const sessionIdRef = useRef<string | null>(null);

  const clearPolling = useCallback(() => {
    if (pollTimerRef.current !== null) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(async (): Promise<SimulatorSession | null> => {
    const sid = sessionIdRef.current;
    try {
      const result = await SimulatorService.status(
        sid ? { sessionId: sid } : { projectId },
      );
      setSession(result);
      if (result && !isPolling(result.status)) {
        clearPolling();
      }
      return result;
    } catch {
      // status 조회 실패는 페이지를 막지 않음 — 폴링은 계속.
      return null;
    }
  }, [projectId, clearPolling]);

  const startPolling = useCallback(() => {
    clearPolling();
    pollTimerRef.current = setInterval(() => {
      fetchStatus();
    }, POLL_INTERVAL_MS);
  }, [clearPolling, fetchStatus]);

  const refreshCctvs = useCallback(async (mapId: number) => {
    if (!Number.isFinite(mapId) || mapId <= 0) {
      setCctvs([]);
      return;
    }
    setLoadingCctvs(true);
    try {
      const list = await SimulatorService.listCctvs(mapId);
      setCctvs(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'CCTV 목록 조회 실패');
      setCctvs([]);
    } finally {
      setLoadingCctvs(false);
    }
  }, []);

  // 비상벨 목록 — CCTV 목록과 동일 패턴. 비상벨 호출 탭과 맵 클릭 레이어에서 사용.
  const refreshEmergencyBells = useCallback(async (mapId: number) => {
    if (!Number.isFinite(mapId) || mapId <= 0) {
      setBells([]);
      return;
    }
    try {
      const list = await SimulatorService.listEmergencyBells(mapId);
      setBells(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : '비상벨 목록 조회 실패');
      setBells([]);
    }
  }, []);

  // 마운트: 맵 목록 + 기존 세션 복원.
  useEffect(() => {
    if (!Number.isFinite(projectId)) return;
    let cancelled = false;

    (async () => {
      setLoadingMaps(true);
      try {
        const list = await SimulatorService.listMaps(projectId);
        if (cancelled) return;
        setMaps(list);
        if (list.length > 0) {
          const firstId = list[0].id;
          setSelectedMapId(firstId);
          refreshCctvs(firstId);
          refreshEmergencyBells(firstId);
        } else {
          setSelectedMapId('');
          setCctvs([]);
          setBells([]);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : '맵 목록 조회 실패');
          setMaps([]);
        }
      } finally {
        if (!cancelled) setLoadingMaps(false);
      }
    })();

    (async () => {
      try {
        const result = await SimulatorService.status({ projectId });
        if (cancelled) return;
        setSession(result);
        sessionIdRef.current = result?.session_id ?? null;
        if (result && isPolling(result.status)) {
          startPolling();
        }
      } catch {
        if (!cancelled) setSession(null);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [projectId, startPolling, refreshCctvs, refreshEmergencyBells]);

  // unmount 시 항상 타이머 정리 (resource leak 금지).
  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, [clearPolling]);

  const handleMapChange = (e: SelectChangeEvent<number | ''>) => {
    const value = e.target.value;
    if (value === '' || value === undefined) {
      setSelectedMapId('');
      setCctvs([]);
      setBells([]);
      return;
    }
    const mapId = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(mapId) || mapId <= 0) {
      setSelectedMapId('');
      setCctvs([]);
      setBells([]);
      return;
    }
    setSelectedMapId(mapId);
    refreshCctvs(mapId);
    refreshEmergencyBells(mapId);
  };

  const handleStart = async () => {
    setError(null);
    setInfo(null);
    if (selectedMapId === '' || !Number.isFinite(selectedMapId) || (selectedMapId as number) <= 0) {
      setError('맵을 선택해 주세요.');
      return;
    }
    setLoadingStart(true);
    try {
      const started = await SimulatorService.start(projectId, selectedMapId as number);
      setSession(started);
      sessionIdRef.current = started.session_id;
      setInfo('시뮬레이션이 시작되었습니다');
      if (isPolling(started.status)) {
        startPolling();
      } else {
        clearPolling();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '시뮬레이터 시작 실패');
    } finally {
      setLoadingStart(false);
    }
  };

  const handleStop = async () => {
    setError(null);
    setInfo(null);
    if (!session?.session_id) {
      setError('중지할 세션이 없습니다.');
      return;
    }
    setLoadingStop(true);
    try {
      const stopped = await SimulatorService.stop(session.session_id);
      setSession({
        ...session,
        status: stopped.status,
      });
      clearPolling();
      setInfo('시뮬레이션이 중지되었습니다');
    } catch (err) {
      setError(err instanceof Error ? err.message : '시뮬레이터 중지 실패');
    } finally {
      setLoadingStop(false);
    }
  };

  const sessionActive = isPolling(session?.status);

  const MODES: { key: number; label: string; icon: React.ReactNode }[] = [
    { key: 0, label: 'CCTV 스트림', icon: <VideocamIcon fontSize="small" /> },
    { key: 1, label: '만공차 체크', icon: <LocalParkingIcon fontSize="small" /> },
    { key: 2, label: '조명 체크', icon: <LightbulbIcon fontSize="small" /> },
    { key: 3, label: '비상벨 호출', icon: <NotificationsActiveIcon fontSize="small" /> },
    { key: 4, label: '통합 자동 테스트', icon: <ChecklistIcon fontSize="small" /> },
  ];

  // 링크된 컴포넌트의 ref가 붙을 때까지 짧게 폴링 대기(마운트가 다음 렌더에서
  // 커밋되므로 setState 직후엔 아직 null 일 수 있다).
  const waitForAutoRunHandle = async (
    getRef: () => SimAutoRunHandle | null,
    timeoutMs = 3000,
  ): Promise<SimAutoRunHandle | null> => {
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const handle = getRef();
      if (handle) return handle;
      await new Promise(r => setTimeout(r, 50));
    }
    return getRef();
  };

  const runCombinedAutoTest = async () => {
    const selected = COMBINED_ORDER.filter(k => combinedTargets[k]);
    if (selected.length === 0 || combinedRunning) return;
    setCombinedRunning(true);
    setCombinedReport(null);
    combinedStopRef.current = false;
    const merged: AutoCheckResult[] = [];
    try {
      for (let i = 0; i < selected.length; i++) {
        if (combinedStopRef.current) break;
        const key = selected[i];
        setCombinedProgress(`${COMBINED_LABEL[key]} 확인 중... (${i + 1}/${selected.length})`);

        // 그 탭이 이미 열려 있으면 기존 인스턴스를 그대로 쓰고, 아니면 이
        // 단계 동안만 화면 밖에 마운트한다(동시에 2개 마운트 금지).
        const alreadyMounted = tab === COMBINED_TAB[key];
        if (!alreadyMounted) setCombinedMounted(key);

        const handle = await waitForAutoRunHandle(() => combinedRefs[key].current);
        if (!handle) {
          merged.push({
            name: COMBINED_LABEL[key],
            passed: false,
            detail: '시뮬레이터를 불러오지 못했습니다.',
            category: COMBINED_LABEL[key],
          });
        } else {
          const results = await handle.runAutoCheck();
          merged.push(...results.map(r => ({ ...r, category: COMBINED_LABEL[key] })));
        }

        if (!alreadyMounted) setCombinedMounted(null);
      }
    } finally {
      setCombinedProgress('');
      setCombinedRunning(false);
      setCombinedReport(merged);
      setCombinedShowReport(true);
    }
  };

  const stopCombinedAutoTest = () => {
    combinedStopRef.current = true;
    lightsAutoRef.current?.stopAutoCheck();
    parkingAutoRef.current?.stopAutoCheck();
    bellAutoRef.current?.stopAutoCheck();
  };

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* 표준 헤더 — 다른 페이지(SitePhotosPage 등)와 동일한 Breadcrumbs 헤더. */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          px: 4,
          py: 1.5,
          bgcolor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <IconButton
          onClick={() => navigate(`/project/${projectId}`)}
          aria-label="대시보드로 돌아가기"
          size="small"
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate('/projects')}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
            현장 목록
          </Link>
          <Link
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/project/${projectId}`)}
            sx={{ cursor: 'pointer', fontWeight: 500 }}
          >
            {projectName || `프로젝트 ${projectId}`}
          </Link>
          <Typography color="text.primary" sx={{ fontWeight: 700 }}>
            시뮬레이터
          </Typography>
        </Breadcrumbs>
      </Paper>

      {/* 본문: 맵 에디터식 3-pane — 왼쪽 테스트 선택 / 가운데 도면 / 오른쪽 설정·실행 */}
      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* LEFT: 맵 선택 + 테스트 선택 */}
        <Box
          sx={{
            width: 240,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 2,
            overflow: 'auto',
          }}
        >
          <FormControl size="small" fullWidth disabled={sessionActive || loadingMaps}>
            <InputLabel id="simulator-map-select-label">맵</InputLabel>
            <Select<number | ''>
              labelId="simulator-map-select-label"
              label="맵"
              value={selectedMapId}
              onChange={handleMapChange}
              inputProps={{ 'aria-label': '맵 선택', 'data-testid': 'simulator-map-select' }}
              displayEmpty
            >
              {maps.length === 0 && (
                <MenuItem value="" disabled>
                  {loadingMaps ? '맵 목록 불러오는 중...' : '등록된 맵이 없습니다'}
                </MenuItem>
              )}
              {maps.map((m) => (
                <MenuItem key={m.id} value={m.id}>
                  {m.name || `맵 #${m.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>
              테스트
            </Typography>
            <List dense disablePadding>
              {MODES.map((m) => (
                <ListItemButton
                  key={m.key}
                  selected={tab === m.key}
                  onClick={() => setTab(m.key)}
                  sx={{ borderRadius: 1, mb: 0.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>{m.icon}</ListItemIcon>
                  <ListItemText primary={m.label} primaryTypographyProps={{ variant: 'body2' }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        </Box>

        {/* CENTER: 도면 (선택한 맵 상시 표시) */}
        <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
          <MapCanvasPanel
            mapId={selectedMapId === '' ? null : selectedMapId}
            height="100%"
            bellClick={tab === 3 ? { bells: effectiveBells } : undefined}
            onCctvNames={setMapCctvNames}
            onCctvList={setMapCctvList}
            onCctvParkingLinks={setCctvParkingLinks}
            onCctvLightLinks={setCctvLightLinks}
            onMapBells={setMapBells}
          />
        </Box>

        {/* RIGHT: 선택한 테스트의 설정/실행 */}
        <Box
          sx={{
            width: 380,
            flexShrink: 0,
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'auto',
            p: 2,
          }}
        >
          {tab === 1 && <FullVacancyCheckSimulator ref={parkingAutoRef} cctvLinks={cctvParkingLinks} />}
          {tab === 2 && (
            <LightsSimulator
              ref={lightsAutoRef}
              cctvs={cctvs}
              mapCctvNames={mapCctvNames}
              mapCctvList={mapCctvList}
              cctvLightLinks={cctvLightLinks}
              mapId={selectedMapId === '' ? null : selectedMapId}
            />
          )}
          {tab === 3 && (
            <EmergencyBellSimulator
              ref={bellAutoRef}
              bells={effectiveBells}
              cctvs={cctvs}
              mapCctvNames={mapCctvNames}
              mapCctvList={mapCctvList}
              cctvLightLinks={cctvLightLinks}
              mapId={selectedMapId === '' ? null : selectedMapId}
            />
          )}
          {tab === 4 && (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                선택한 시뮬레이터를 순서대로(조명 → 만공차등 → 비상벨) 하나씩 자동 실행하고,
                이상이 있는 항목을 모아 하나의 리포트로 보여줍니다.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  실행할 시뮬레이터
                </Typography>
                <Stack>
                  {COMBINED_ORDER.map((key) => (
                    <FormControlLabel
                      key={key}
                      control={
                        <Checkbox
                          size="small"
                          checked={combinedTargets[key]}
                          disabled={combinedRunning}
                          onChange={(e) =>
                            setCombinedTargets((prev) => ({ ...prev, [key]: e.target.checked }))
                          }
                        />
                      }
                      label={COMBINED_LABEL[key]}
                    />
                  ))}
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                  {combinedRunning ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={stopCombinedAutoTest}
                    >
                      정지
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<AutoRunIcon />}
                      onClick={runCombinedAutoTest}
                      disabled={COMBINED_ORDER.every((k) => !combinedTargets[k])}
                    >
                      전체 자동 실행
                    </Button>
                  )}
                </Stack>
                {combinedProgress && (
                  <Alert severity="info" sx={{ mt: 1.5 }}>
                    {combinedProgress}
                  </Alert>
                )}
                {!combinedRunning && combinedReport && (
                  <Alert
                    severity={combinedReport.every((r) => r.passed) ? 'success' : 'warning'}
                    sx={{ mt: 1.5 }}
                    action={
                      <Button size="small" onClick={() => setCombinedShowReport(true)}>
                        리포트 보기
                      </Button>
                    }
                  >
                    {combinedReport.filter((r) => r.passed).length}/{combinedReport.length} 통과
                  </Alert>
                )}
              </Paper>

              {/* 오케스트레이터가 현재 단계 시뮬레이터를 화면 밖에서 실행 —
                  그 탭이 이미 열려 있으면(위에서 렌더됨) 여기선 중복 마운트하지 않는다. */}
              <Box sx={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
                {combinedMounted === 'lights' && tab !== COMBINED_TAB.lights && (
                  <LightsSimulator
                    ref={lightsAutoRef}
                    cctvs={cctvs}
                    mapCctvNames={mapCctvNames}
                    mapCctvList={mapCctvList}
                    cctvLightLinks={cctvLightLinks}
                    mapId={selectedMapId === '' ? null : selectedMapId}
                  />
                )}
                {combinedMounted === 'parking' && tab !== COMBINED_TAB.parking && (
                  <FullVacancyCheckSimulator ref={parkingAutoRef} cctvLinks={cctvParkingLinks} />
                )}
                {combinedMounted === 'bell' && tab !== COMBINED_TAB.bell && (
                  <EmergencyBellSimulator
                    ref={bellAutoRef}
                    bells={effectiveBells}
                    cctvs={cctvs}
                    mapCctvNames={mapCctvNames}
                    mapCctvList={mapCctvList}
                    cctvLightLinks={cctvLightLinks}
                    mapId={selectedMapId === '' ? null : selectedMapId}
                  />
                )}
              </Box>

              <AutoTestReportDialog
                open={combinedShowReport}
                title="통합 자동 테스트 리포트"
                results={combinedReport ?? []}
                onClose={() => setCombinedShowReport(false)}
              />
            </Stack>
          )}
          {tab === 0 && (
          <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo(null)}>
          {info}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
          세션 제어
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleStart}
            disabled={sessionActive || loadingStart || selectedMapId === ''}
            data-testid="simulator-start-btn"
          >
            {loadingStart ? '시작 중...' : '시작'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={handleStop}
            disabled={!sessionActive || loadingStop}
            data-testid="simulator-stop-btn"
          >
            {loadingStop ? '중지 중...' : '중지'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              상태
            </Typography>
            <Chip
              size="small"
              label={statusLabel(session?.status)}
              color={statusColor(session?.status)}
              data-testid="simulator-status-chip"
            />
          </Stack>
        </Stack>

        {session && (
          <Box sx={{ mt: 2 }}>
            <Stack direction="row" spacing={3} flexWrap="wrap">
              <Typography variant="body2" color="text.secondary">
                세션: <strong>{session.session_id}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                맵 ID: <strong>{session.map_id}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CCTV 수: <strong>{session.cctv_count}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                시작: <strong>{session.started_at}</strong>
              </Typography>
            </Stack>

            {session.rtsp_base_url && (
              <Box sx={{ mt: 1.5 }} data-testid="simulator-rtsp-base">
                <Typography variant="body2" color="text.secondary" component="span">
                  RTSP 베이스:{' '}
                </Typography>
                <Typography
                  variant="body2"
                  component="code"
                  sx={{
                    fontFamily: 'monospace',
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 0.5,
                    bgcolor: 'action.hover',
                  }}
                >
                  {session.rtsp_base_url}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  운영자는 위 베이스에 `/cctv-&lt;id&gt;` path 를 붙여 사용합니다.
                </Typography>
              </Box>
            )}

            {session.status === 'error' && session.error_message && (
              <Alert
                severity="error"
                sx={{ mt: 1.5 }}
                data-testid="simulator-error-message"
              >
                {session.error_message}
              </Alert>
            )}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1">CCTV 목록 (선택한 맵)</Typography>
          {loadingCctvs && <CircularProgress size={18} />}
        </Stack>
        <TableContainer>
          <Table size="small" aria-label="CCTV 목록">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>이름</TableCell>
                <TableCell>IP</TableCell>
                <TableCell>포트</TableCell>
                <TableCell>모델</TableCell>
                <TableCell>해상도</TableCell>
                <TableCell>위치</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loadingCctvs && cctvs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {selectedMapId === ''
                        ? '맵을 선택해 주세요.'
                        : '이 맵에 배치된 CCTV 가 없습니다.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {cctvs.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>{c.id}</TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.ip_address}</TableCell>
                  <TableCell>{c.port ?? '-'}</TableCell>
                  <TableCell>{c.model ?? '-'}</TableCell>
                  <TableCell>{c.resolution ?? '-'}</TableCell>
                  <TableCell>{c.location ?? '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </>
      )}
        </Box>
      </Box>
    </Box>
  );
};

export default SimulatorPage;
